import { useRef, useState, useEffect } from "react";
import "../styles/Music.scss";
import musicFile from "../assets/nightcall.mp3";
import { BsVolumeMuteFill } from "react-icons/bs";
import imgPortfolio from "../assets/portfolio.png";
import imgDA from "../assets/DA.png";
import imgFB from "../assets/fb.png";
import tapeImg from "../assets/tape.png";

const PixelHeart = () => (
  <svg width="22" height="20" viewBox="0 0 11 10" className="pixel-heart" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="0" width="2" height="1" fill="#ff69b4"/>
    <rect x="5" y="0" width="2" height="1" fill="#ff69b4"/>
    <rect x="0" y="1" width="4" height="1" fill="#ff69b4"/>
    <rect x="4" y="1" width="1" height="1" fill="#c0006a"/>
    <rect x="5" y="1" width="4" height="1" fill="#ff69b4"/>
    <rect x="0" y="2" width="9" height="1" fill="#ff69b4"/>
    <rect x="1" y="2" width="2" height="1" fill="#ffb6d9"/>
    <rect x="0" y="3" width="9" height="1" fill="#ff69b4"/>
    <rect x="1" y="3" width="1" height="1" fill="#ffb6d9"/>
    <rect x="1" y="4" width="7" height="1" fill="#ff69b4"/>
    <rect x="2" y="5" width="5" height="1" fill="#ff69b4"/>
    <rect x="3" y="6" width="3" height="1" fill="#ff69b4"/>
    <rect x="4" y="7" width="1" height="1" fill="#ff69b4"/>
  </svg>
);

const rand = (min, max) => Math.random() * (max - min) + min;
const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

const SYMBOL_COLORS = [
  { color: "#00ffff", shadow: "#00ffff" },
  { color: "#ff0055", shadow: "#ff0055" },
  { color: "#ffdd00", shadow: "#ffdd00" },
  { color: "#ff9900", shadow: "#ff9900" },
  { color: "#ffffff", shadow: "#fff" },
];

const ELEMENTS = Array.from({ length: 23 }, () => ({
  top:      `${rand(0, 95)}%`,
  left:     `${rand(0, 95)}%`,
  ...pick(SYMBOL_COLORS),
  rotate:   rand(-20, 20),
  size:     rand(10, 16),
  symbol:   pick(["×", "○", "□", "△"]),
  tx:       `${rand(-40, 40)}px`,
  ty:       `${rand(-40, 40)}px`,
  duration: rand(8, 22),
  delay:    rand(0, 12),
}));

export default function BackgroundMusic() {
  const audioRef   = useRef(null);
  const canvasRef  = useRef(null);
  const [playing,    setPlaying]    = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [displayed,  setDisplayed]  = useState("");
  const [fading,     setFading]     = useState(false);
  const fullText = "PRESS START";

  // Étoiles aléatoires
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const draw = () => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const colors = ["#ffffff","#ffffff","#ffffff","#00ffff","#ff00ff","#ffffff"];
      for (let i = 0; i < 120; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height * 0.6;
        const r = Math.random() * 1.2 + 0.3;
        const c = colors[Math.floor(Math.random() * colors.length)];
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = c;
        ctx.globalAlpha = Math.random() * 0.5 + 0.5;
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };
    draw();
  }, [showSplash]);

  // Typewriter
  useEffect(() => {
    if (!showSplash) return;
    let i = 0;
    setDisplayed("");
    const type = () => {
      const interval = setInterval(() => {
        setDisplayed(fullText.slice(0, i + 1));
        i++;
        if (i === fullText.length) {
          clearInterval(interval);
          setTimeout(() => { i = 0; setDisplayed(""); type(); }, 3000);
        }
      }, 110);
    };
    type();
  }, [showSplash]);

  const splashRef = useRef(null);

  const handleEnter = () => {
    if (fading) return;
    if (audioRef.current) {
      audioRef.current.volume = 0.3;
      audioRef.current.play().then(() => setPlaying(true)).catch(() => {});
    }
    // Force le reflow avant d'appliquer la transition
    const el = splashRef.current;
    if (el) {
      el.style.transition = "opacity 1.2s ease";
      // eslint-disable-next-line no-unused-expressions
      el.offsetHeight; // force reflow
      el.style.opacity = "0";
    }
    setFading(true);
    setTimeout(() => setShowSplash(false), 1300);
  };

  const toggle = () => {
    if (!audioRef.current) return;
    playing ? audioRef.current.pause() : audioRef.current.play();
    setPlaying(!playing);
  };

  return (
    <>
      <audio ref={audioRef} loop>
        <source src={musicFile} type="audio/mpeg" />
      </audio>

      {showSplash && (
        <div ref={splashRef} className={`splash ${fading ? "splash--fading" : ""}`} onClick={handleEnter}>
          <div className="splash__scanlines" />
          <canvas ref={canvasRef} className="splash__stars" />
          <div className="splash__grid" />

          {ELEMENTS.map((el, idx) => (
            <span
              key={idx}
              className="splash__el"
              style={{
                top: el.top,
                left: el.left,
                color: el.color,
                textShadow: `0 0 8px ${el.shadow}, 0 0 16px ${el.shadow}`,
                fontSize: `${el.size}px`,
                '--rot': `${el.rotate}deg`,
                '--tx': el.tx,
                '--ty': el.ty,
                animation: `float-symbol ${el.duration}s ease-in-out ${el.delay}s infinite`,
              }}
            >
              {el.symbol}
            </span>
          ))}

          <div className="splash__logo">
            <img src={imgDA}        className="splash__logo-da"        alt="D.A. & Graphiste" />
            <img src={imgPortfolio} className="splash__logo-portfolio" alt="Portfolio"         />
            <img src={imgFB}        className="splash__logo-fb"        alt="Fabien Bazire"     />
          </div>

          <div className="splash__content">
            <p className="splash__score">HI-SCORE &nbsp;&nbsp; 999999</p>
            <p className="splash__hint">
              <span>{displayed}</span>
              <span className="splash__cursor">_</span>
            </p>
            <div className="splash__lives">
              <PixelHeart />
              <PixelHeart />
              <PixelHeart />
            </div>
          </div>

          <p className="splash__copyright">{`© ${new Date().getFullYear()} FABIEN BAZIRE`}</p>
        </div>
      )}

      {!showSplash && (
        <button
          onClick={toggle}
          className={`music-btn ${playing ? "music-btn--playing" : ""}`}
          aria-label={playing ? "Couper la musique" : "Lancer la musique"}
        >
          <span className="music-btn__icon">
            {playing ? <img src={tapeImg} alt="cassette" className="music-btn__tape" /> : <BsVolumeMuteFill />}
          </span>
          <span className="music-btn__tooltip">
            {playing ? "Couper la musique" : "Lancer la musique"}
          </span>
        </button>
      )}
    </>
  );
}